import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import type {
  ColumnDefinition,
  ControlPaneDisplay,
  TableDefinitionColumnOptions,
} from '@/tableDefinitionEditor/tableDefinition.types';
import { createSelector } from 'reselect';
import { TABLE_DEFINITION_COLUMN_OPTIONS } from '@/tableDefinitionEditor/tableDefinition.constants';

/**
 * Base class store for the table definition editor
 * Table definition state is not stored in worksteps, so we separate the logic of what table is being looked at
 * here, which is stored in the workstep, while the table definition state is stored in the table definition store
 * which is updated when we receive messages from the server on the socket for table definitions.
 * This base class contains all handler functions, getters and state management that can be used in other editor stores
 */
export abstract class BaseTableEditorStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  initialize() {
    this.state = this.immutable({
      tableId: '',
      showConfigureSearch: false,
      columnToEdit: undefined,
      columnToEditId: undefined,
      enableTreeCreation: false,
      selectedColumnOption: undefined,
      visibleControlPane: 'column',
      shortcutSourceColumn: undefined,
      restrictColumnMoving: this.monkey(
        ['selectedColumnOption'],
        ['columnToEditId'],
        (
          selectedColumnOption: TableDefinitionColumnOptions | undefined,
          columnToEditId: string | undefined,
        ): boolean => {
          const isEditColumnAction =
            selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.EDIT_COLUMN.value && !!columnToEditId;
          const isAddColumnAction = selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.ADD_COLUMN.value;
          const isAddPropertyToColumn =
            selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.ADD_PROPERTY_TO_COLUMN.value;
          const isAddOrModifyColumn = isEditColumnAction || isAddColumnAction || isAddPropertyToColumn;
          const isEditModel = selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.EDIT_MODEL.value;
          const isAddModel = selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.CREATE_MODEL.value;
          const isApplyModel = selectedColumnOption?.value === TABLE_DEFINITION_COLUMN_OPTIONS.APPLY_MODEL.value;
          const isAddOrModifyModel = isAddModel || isEditModel || isApplyModel;
          return isAddOrModifyModel || isAddOrModifyColumn;
        },
      ),
    });
  }

  get tableId(): string {
    return this.state.get('tableId');
  }

  get showConfigureSearch(): boolean {
    return this.state.get('showConfigureSearch');
  }

  get restrictColumnMoving(): boolean {
    return this.state.get('restrictColumnMoving');
  }

  abstract get columnToEdit(): ColumnDefinition | undefined;

  getColumnToEdit: (state: {
    columnToEditId: string | undefined;
    existingColumns: ColumnDefinition[];
  }) => ColumnDefinition | undefined = createSelector(
    (state: { columnToEditId: string | undefined; existingColumns: ColumnDefinition[] }) => state.columnToEditId,
    (state: { columnToEditId: string | undefined; existingColumns: ColumnDefinition[] }) => state.existingColumns,
    (columnToEditId: string | undefined, existingColumns: ColumnDefinition[]) => {
      if (!columnToEditId) {
        return undefined;
      }
      return existingColumns.find((column: ColumnDefinition) => column.id === columnToEditId);
    },
  );

  get columnToEditId(): string | undefined {
    return this.state.get('columnToEditId');
  }

  get showColumnProperties(): boolean {
    return this.state.get('showColumnProperties');
  }

  get enableTreeCreation(): boolean {
    return this.state.get('enableTreeCreation');
  }

  get selectedColumnOption(): TableDefinitionColumnOptions | undefined {
    return this.state.get('selectedColumnOption');
  }

  get visibleControlPane(): ControlPaneDisplay {
    return this.state.get('visibleControlPane');
  }

  get shortcutSourceColumn(): ColumnDefinition | undefined {
    return this.state.get('shortcutSourceColumn');
  }

  protected readonly handlers = {};

  setTableId = (payload: { tableId: string }) => {
    this.state.set('tableId', payload.tableId);
  };

  setShowConfigureSearch = (payload: { showConfigureSearch: boolean }) => {
    this.state.set('showConfigureSearch', payload.showConfigureSearch);
  };

  setColumnToEditId = (payload: { columnToEditId: string | undefined }) => {
    this.state.set('columnToEditId', payload.columnToEditId);
  };

  setShowColumnProperties = (payload: { showColumnProperties: boolean }) => {
    this.state.set('showColumnProperties', payload.showColumnProperties);
  };

  setEnableTreeCreation = ({ enableTreeCreation }: { enableTreeCreation: boolean }) => {
    this.state.set('enableTreeCreation', enableTreeCreation);
  };

  setSelectedColumnOption = ({
    selectedColumnOption,
  }: {
    selectedColumnOption: TableDefinitionColumnOptions | undefined;
  }) => {
    this.state.set('selectedColumnOption', selectedColumnOption);
  };

  setShortcutSourceColumn = (payload: { shortcutSourceColumn: ColumnDefinition | undefined }) => {
    this.state.set('shortcutSourceColumn', payload.shortcutSourceColumn);
  };

  setControlPane = (payload: { controlPane: ControlPaneDisplay }) => {
    this.state.set('visibleControlPane', payload.controlPane);
  };

  reset = () => {
    this.initialize();
  };

  /**
   * Dehydrates the item by retrieving the current set parameters in view
   *
   * @returns An object with the state properties as JSON
   */
  dehydrate() {
    return this.state.serialize();
  }

  /**
   * Rehydrates item from dehydrated state
   *
   * @param dehydratedState - State object that should be restored
   */
  rehydrate(dehydratedState: Record<string, any>) {
    this.state.merge(dehydratedState);
  }
}
